import React, { useEffect, useState } from 'react';
import { FormField } from './FormField';
import { PlusIcon, TrashIcon, CalendarIcon, MailIcon, SmartphoneIcon, CalendarDaysIcon, UsersIcon, BuildingIcon, PercentIcon, DollarSignIcon, AlertCircleIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { SegmentedControl } from './SegmentedControl';
export interface Recipient {
  id: number;
  email: string;
  phone: string;
}
export interface Supplier {
  id: number;
  name: string;
  email: string;
  amount: string;
  percentage?: string;
}
export interface Installment {
  id: number;
  amount: string;
  dueDate: string;
  sendLinkDate: string;
  description: string;
  recipient?: Recipient;
}
interface InstallmentPaymentProps {
  onExpiryDateChange?: (date: string) => void;
}
export function InstallmentPayment({
  onExpiryDateChange
}: InstallmentPaymentProps) {
  const [installments, setInstallments] = useState<Installment[]>([{
    id: 1,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 1,
      email: '',
      phone: ''
    }
  }, {
    id: 2,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 2,
      email: '',
      phone: ''
    }
  }]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([{
    id: 1,
    name: '',
    email: '',
    amount: '',
    percentage: '0'
  }]);
  const [splitType, setSplitType] = useState<'dates' | 'recipients' | 'commission'>('dates');
  const [beneficiaryType, setBeneficiaryType] = useState<'percentage' | 'fixed'>('percentage');
  const [feeType, setFeeType] = useState<'supplier' | 'merchant' | 'split'>('supplier');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [totalPaymentAmount, setTotalPaymentAmount] = useState('');
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [supplierCommissionTotal, setSupplierCommissionTotal] = useState(0);
  const [showDatePickers, setShowDatePickers] = useState<{
    [key: number]: {
      due: boolean;
      send: boolean;
    };
  }>({});
  // This would come from settings in a real app
  const defaultExpiryDays = 30;
  // Validate commission amounts don't exceed total payment
  useEffect(() => {
    if (splitType === 'commission' && totalPaymentAmount) {
      const totalAmount = parseFloat(totalPaymentAmount) || 0;
      // Calculate total commission amount
      const commissionTotal = suppliers.reduce((total, supplier) => {
        if (beneficiaryType === 'percentage') {
          // For percentage, we'll check in another validation
          return total;
        } else {
          // For fixed amount
          return total + (parseFloat(supplier.amount) || 0);
        }
      }, 0);
      setSupplierCommissionTotal(commissionTotal);
      if (beneficiaryType === 'fixed' && commissionTotal > totalAmount) {
        setCommissionError('Total commission amount cannot exceed the total payment amount');
      } else if (beneficiaryType === 'percentage') {
        const percentageTotal = suppliers.reduce((total, supplier) => {
          return total + (parseFloat(supplier.percentage || '0') || 0);
        }, 0);
        if (percentageTotal > 100) {
          setCommissionError('Total commission percentage cannot exceed 100%');
        } else {
          setCommissionError(null);
        }
      } else {
        setCommissionError(null);
      }
    }
  }, [splitType, totalPaymentAmount, suppliers, beneficiaryType]);
  // Calculate send link date when due date changes
  const calculateSendLinkDate = (dueDateStr: string): string => {
    if (!dueDateStr) return '';
    try {
      // Parse the due date
      const [month, day, year] = dueDateStr.split('/').map(part => parseInt(part));
      const dueDate = new Date(year, month - 1, day);
      // Subtract the default expiry days
      const sendDate = new Date(dueDate);
      sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
      // Format the date as MM/DD/YYYY
      const sendMonth = (sendDate.getMonth() + 1).toString().padStart(2, '0');
      const sendDay = sendDate.getDate().toString().padStart(2, '0');
      const sendYear = sendDate.getFullYear();
      return `${sendMonth}/${sendDay}/${sendYear}`;
    } catch (error) {
      // If there's any error parsing, return empty string
      return '';
    }
  };
  const addInstallment = () => {
    const newId = Math.max(...installments.map(i => i.id), 0) + 1;
    setInstallments([...installments, {
      id: newId,
      amount: '',
      dueDate: '',
      sendLinkDate: '',
      description: '',
      recipient: {
        id: newId,
        email: '',
        phone: ''
      }
    }]);
  };
  const addSupplier = () => {
    const newId = Math.max(...suppliers.map(s => s.id), 0) + 1;
    setSuppliers([...suppliers, {
      id: newId,
      name: '',
      email: '',
      amount: '',
      percentage: '0'
    }]);
  };
  const removeInstallment = (id: number) => {
    if (installments.length > 1) {
      setInstallments(installments.filter(i => i.id !== id));
    }
  };
  const removeSupplier = (id: number) => {
    if (suppliers.length > 1) {
      setSuppliers(suppliers.filter(s => s.id !== id));
    }
  };
  const updateInstallment = (id: number, field: string, value: string) => {
    setInstallments(installments.map(installment => {
      if (installment.id === id) {
        const updatedInstallment = {
          ...installment,
          [field]: value
        };
        // If we're updating the due date and it's a valid date, update the send link date
        if (field === 'dueDate' && value) {
          updatedInstallment.sendLinkDate = calculateSendLinkDate(value);
        }
        return updatedInstallment;
      }
      return installment;
    }));
  };
  const updateSupplier = (id: number, field: string, value: string) => {
    setSuppliers(suppliers.map(supplier => supplier.id === id ? {
      ...supplier,
      [field]: value
    } : supplier));
  };
  const updateRecipient = (installmentId: number, field: 'email' | 'phone', value: string) => {
    setInstallments(installments.map(installment => installment.id === installmentId && installment.recipient ? {
      ...installment,
      recipient: {
        ...installment.recipient,
        [field]: value
      }
    } : installment));
  };
  const splitOptions = [{
    value: 'dates',
    label: 'By payment schedule',
    icon: <CalendarDaysIcon size={16} data-id="element-840" />
  }, {
    value: 'recipients',
    label: 'By multiple people',
    icon: <UsersIcon size={16} data-id="element-841" />
  }, {
    value: 'commission',
    label: 'By commission',
    icon: <PercentIcon size={16} data-id="element-842" />
  }];
  const beneficiaryOptions = [{
    value: 'percentage',
    label: 'Percentage',
    icon: <PercentIcon size={16} data-id="element-843" />
  }, {
    value: 'fixed',
    label: 'Fixed amount',
    icon: <DollarSignIcon size={16} data-id="element-844" />
  }];
  const totalAmount = installments.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  // Mock suppliers list for dropdown
  const suppliersList = [{
    id: 1,
    name: 'ABC Suppliers',
    email: 'abc@example.com'
  }, {
    id: 2,
    name: 'XYZ Merchants',
    email: 'xyz@example.com'
  }, {
    id: 3,
    name: '123 Distributors',
    email: '123@example.com'
  }, {
    id: 4,
    name: 'Global Partners',
    email: 'global@example.com'
  }];
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  const handleTotalPaymentAmountChange = (value: string) => {
    setTotalPaymentAmount(value);
  };
  const getCommissionAmountDisplay = () => {
    if (beneficiaryType === 'fixed') {
      return `$${supplierCommissionTotal.toFixed(2)} of $${parseFloat(totalPaymentAmount || '0').toFixed(2)}`;
    } else {
      const percentageTotal = suppliers.reduce((total, supplier) => {
        return total + (parseFloat(supplier.percentage || '0') || 0);
      }, 0);
      return `${percentageTotal.toFixed(2)}% of payment`;
    }
  };
  // Date picker helpers
  const toggleDatePicker = (id: number, type: 'due' | 'send') => {
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: prev[id] ? !prev[id][type] : true
      }
    }));
  };
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const handleDateSelect = (id: number, field: 'dueDate' | 'sendLinkDate', date: Date) => {
    updateInstallment(id, field, formatDate(date));
    // If selecting due date, update send link date
    if (field === 'dueDate') {
      const sendDate = new Date(date);
      sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
      updateInstallment(id, 'sendLinkDate', formatDate(sendDate));
    }
    // Close the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field === 'dueDate' ? 'due' : 'send']: false
      }
    }));
  };
  const generateCalendar = (dateStr: string) => {
    const today = new Date();
    const selectedDate = dateStr ? parseDate(dateStr) : today;
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        // Fill remaining days of the week
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: selectedDate,
      weeks
    };
  };
  const parseDate = (dateString: string): Date => {
    if (!dateString) return new Date();
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date, selectedDateStr: string): boolean => {
    if (!selectedDateStr) return false;
    const selectedDate = parseDate(selectedDateStr);
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handlePrevMonth = (calendar, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() - 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  const handleNextMonth = (calendar, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() + 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  return <div className="space-y-6" data-id="element-845">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-846">
        <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-847">
          Installment payment
        </h2>
        <FormField label="How would you like to split this payment?" tooltip="Choose how to split your payment" data-id="element-848">
          <SegmentedControl options={splitOptions} value={splitType} onChange={value => setSplitType(value as 'dates' | 'recipients' | 'commission')} className="w-full md:w-auto" data-id="element-849" />
        </FormField>
        <FormField label="Order reference" tooltip="A reference number or name for this payment" data-id="element-850">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Order #1234 or Project Name" data-id="element-851" />
        </FormField>
        {splitType === 'commission' && <>
            <FormField label="Total payment amount" required tooltip="The total amount that will be paid" data-id="element-852">
              <div className="relative" data-id="element-853">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-854">
                  <span className="text-gray-500" data-id="element-855">$</span>
                </div>
                <input type="text" className="block w-full pl-8 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={totalPaymentAmount} onChange={e => handleTotalPaymentAmountChange(e.target.value)} data-id="element-856" />
              </div>
            </FormField>
            <FormField label="Commission type" tooltip="Choose whether to use percentage or fixed amount for commissions" data-id="element-857">
              <SegmentedControl options={beneficiaryOptions} value={beneficiaryType} onChange={value => setBeneficiaryType(value as 'percentage' | 'fixed')} className="w-full md:w-auto" data-id="element-858" />
            </FormField>
          </>}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-859">
        <div className="mb-3" data-id="element-860">
          <div className="flex justify-between items-center" data-id="element-861">
            <h3 className="text-md font-medium text-gray-700" data-id="element-862">
              {splitType === 'dates' ? 'Payment schedule' : splitType === 'recipients' ? 'Payment recipients' : 'Commission details'}
            </h3>
            <div className="text-sm text-gray-500" data-id="element-863">
              {splitType === 'commission' ? `Commission: ${getCommissionAmountDisplay()}` : `Total: $${totalAmount.toFixed(2)}`}
            </div>
          </div>
        </div>
        {/* Commission section */}
        {splitType === 'commission' && <div className="space-y-3" data-id="element-864">
            {commissionError && <div className="p-3 bg-red-50 text-red-700 rounded-md flex items-start" data-id="element-865">
                <AlertCircleIcon size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-866" />
                <span className="text-sm" data-id="element-867">{commissionError}</span>
              </div>}
            <div className="p-3 bg-blue-50 text-blue-700 rounded-md flex items-start mb-4" data-id="element-868">
              <BuildingIcon size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-869" />
              <div className="text-sm" data-id="element-870">
                <p data-id="element-871">
                  Commission splitting allows you to distribute a payment
                  between your business and your partners or suppliers. You can:
                </p>
                <ul className="list-disc ml-5 mt-2" data-id="element-872">
                  <li data-id="element-873">Set commission as a percentage or fixed amount</li>
                  <li data-id="element-874">Determine who pays the transaction fees</li>
                  <li data-id="element-875">
                    Automatically split funds when the payment is processed
                  </li>
                </ul>
              </div>
            </div>
            {suppliers.map((supplier, index) => <div key={supplier.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-md" data-id="element-876">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full font-medium text-sm" data-id="element-877">
                  {index + 1}
                </div>
                <div className="flex-1" data-id="element-878">
                  <div className="grid grid-cols-1 gap-3" data-id="element-879">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2" data-id="element-880">
                      <div data-id="element-881">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-882">
                          Select partner/merchant
                        </label>
                        <select className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={supplier.name} onChange={e => {
                    const selected = suppliersList.find(s => s.name === e.target.value);
                    if (selected) {
                      updateSupplier(supplier.id, 'name', selected.name);
                      updateSupplier(supplier.id, 'email', selected.email);
                    }
                  }} data-id="element-883">
                          <option value="" data-id="element-884">Select a partner</option>
                          {suppliersList.map(s => <option key={s.id} value={s.name} data-id="element-885">
                              {s.name}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-886">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-887">
                          Email
                        </label>
                        <input type="email" className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-gray-100" placeholder="Partner email" value={supplier.email} readOnly data-id="element-888" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-889">
                      <div data-id="element-890">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-891">
                          {beneficiaryType === 'percentage' ? 'Commission percentage' : 'Fixed amount'}
                        </label>
                        <div className="relative" data-id="element-892">
                          {beneficiaryType === 'percentage' ? <>
                              <input type="number" className="block w-full pr-8 py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0" min="0" max="100" value={supplier.percentage} onChange={e => updateSupplier(supplier.id, 'percentage', e.target.value)} data-id="element-893" />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-894">
                                <span className="text-gray-500" data-id="element-895">%</span>
                              </div>
                            </> : <>
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-896">
                                <span className="text-gray-500" data-id="element-897">$</span>
                              </div>
                              <input type="text" className="block w-full pl-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={supplier.amount} onChange={e => updateSupplier(supplier.id, 'amount', e.target.value)} data-id="element-898" />
                            </>}
                        </div>
                        {beneficiaryType === 'percentage' && parseFloat(supplier.percentage || '0') > 0 && totalPaymentAmount && <div className="text-xs text-gray-500 mt-1" data-id="element-899">
                              ≈ $
                              {(parseFloat(totalPaymentAmount) * parseFloat(supplier.percentage || '0') / 100).toFixed(2)}
                            </div>}
                      </div>
                      <div data-id="element-900">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-901">
                          Description
                        </label>
                        <input type="text" className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Commission for services" value={supplier.description || ''} onChange={e => updateSupplier(supplier.id, 'description', e.target.value)} data-id="element-902" />
                      </div>
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => removeSupplier(supplier.id)} className="text-gray-400 hover:text-red-500 mt-2" disabled={suppliers.length <= 1} data-id="element-903">
                  <TrashIcon size={18} data-id="element-904" />
                </button>
              </div>)}
            <button type="button" onClick={addSupplier} className="mt-3 flex items-center text-sm text-blue-600 hover:text-blue-800" data-id="element-905">
              <PlusIcon size={16} className="mr-1" data-id="element-906" />
              Add another partner
            </button>
          </div>}
        {/* Dates and Recipients sections (existing code) */}
        {(splitType === 'dates' || splitType === 'recipients') && <div className="space-y-3" data-id="element-907">
            {splitType === 'dates' && <div className="p-3 bg-blue-50 text-blue-700 rounded-md flex items-start mb-4" data-id="element-908">
                <InfoIcon size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-909" />
                <div className="text-sm" data-id="element-910">
                  <p data-id="element-911">
                    Payment links will be automatically sent on the "Send link
                    on" date. By default, links are sent {defaultExpiryDays}{' '}
                    days before the due date.
                  </p>
                </div>
              </div>}
            {installments.map((installment, index) => <div key={installment.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-md" data-id="element-912">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full font-medium text-sm" data-id="element-913">
                  {index + 1}
                </div>
                <div className="flex-1" data-id="element-914">
                  <div className="grid grid-cols-1 gap-3" data-id="element-915">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-916">
                      <div data-id="element-917">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-918">
                          Amount
                        </label>
                        <div className="relative" data-id="element-919">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-920">
                            <span className="text-gray-500" data-id="element-921">$</span>
                          </div>
                          <input type="text" className="block w-full pl-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={installment.amount} onChange={e => updateInstallment(installment.id, 'amount', e.target.value)} data-id="element-922" />
                        </div>
                      </div>
                      {splitType === 'dates' ? <>
                          <div className="relative" data-id="element-923">
                            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-924">
                              Due date
                            </label>
                            <div className="relative" data-id="element-925">
                              <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" value={installment.dueDate} onChange={e => updateInstallment(installment.id, 'dueDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'due')} readOnly data-id="element-926" />
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-927">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-928" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'due')} data-id="element-929">
                                {showDatePickers[installment.id]?.due ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-930" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-931" />}
                              </div>
                            </div>
                            {/* Date Picker for Due Date */}
                            {showDatePickers[installment.id]?.due && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 w-full" data-id="element-932">
                                {(() => {
                        const calendar = generateCalendar(installment.dueDate);
                        return <>
                                      <div className="flex justify-between items-center mb-2" data-id="element-933">
                                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => handlePrevMonth(calendar, installment.id, 'due')} data-id="element-934">
                                          <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-935" />
                                        </button>
                                        <div className="font-medium" data-id="element-936">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => handleNextMonth(calendar, installment.id, 'due')} data-id="element-937">
                                          <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-938" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-939">
                                        <div className="text-gray-500" data-id="element-940">Su</div>
                                        <div className="text-gray-500" data-id="element-941">Mo</div>
                                        <div className="text-gray-500" data-id="element-942">Tu</div>
                                        <div className="text-gray-500" data-id="element-943">We</div>
                                        <div className="text-gray-500" data-id="element-944">Th</div>
                                        <div className="text-gray-500" data-id="element-945">Fr</div>
                                        <div className="text-gray-500" data-id="element-946">Sa</div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-947">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm
                                                  ${isSelected(date, installment.dueDate) ? 'bg-blue-600 text-white' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.dueDate) ? 'border border-blue-600 text-blue-600' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                                                `} onClick={() => handleDateSelect(installment.id, 'dueDate', date)} data-id="element-948">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-949"></div>))}
                                      </div>
                                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-950">
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 7);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-951">
                                          7 days
                                        </button>
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 30);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-952">
                                          30 days
                                        </button>
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 90);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-953">
                                          90 days
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>
                          <div className="relative" data-id="element-954">
                            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-955">
                              Send link on
                            </label>
                            <div className="relative" data-id="element-956">
                              <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" value={installment.sendLinkDate} onChange={e => updateInstallment(installment.id, 'sendLinkDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'send')} readOnly data-id="element-957" />
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-958">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-959" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'send')} data-id="element-960">
                                {showDatePickers[installment.id]?.send ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-961" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-962" />}
                              </div>
                            </div>
                            {/* Date Picker for Send Link Date */}
                            {showDatePickers[installment.id]?.send && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 w-full" data-id="element-963">
                                {(() => {
                        const calendar = generateCalendar(installment.sendLinkDate);
                        return <>
                                      <div className="flex justify-between items-center mb-2" data-id="element-964">
                                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => handlePrevMonth(calendar, installment.id, 'send')} data-id="element-965">
                                          <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-966" />
                                        </button>
                                        <div className="font-medium" data-id="element-967">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => handleNextMonth(calendar, installment.id, 'send')} data-id="element-968">
                                          <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-969" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-970">
                                        <div className="text-gray-500" data-id="element-971">Su</div>
                                        <div className="text-gray-500" data-id="element-972">Mo</div>
                                        <div className="text-gray-500" data-id="element-973">Tu</div>
                                        <div className="text-gray-500" data-id="element-974">We</div>
                                        <div className="text-gray-500" data-id="element-975">Th</div>
                                        <div className="text-gray-500" data-id="element-976">Fr</div>
                                        <div className="text-gray-500" data-id="element-977">Sa</div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-978">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm
                                                  ${isSelected(date, installment.sendLinkDate) ? 'bg-blue-600 text-white' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.sendLinkDate) ? 'border border-blue-600 text-blue-600' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                                                `} onClick={() => handleDateSelect(installment.id, 'sendLinkDate', date)} data-id="element-979">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-980"></div>))}
                                      </div>
                                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-981">
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 7);
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-982">
                                          7 days
                                        </button>
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 14);
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-983">
                                          14 days
                                        </button>
                                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                              const date = new Date();
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-984">
                                          Today
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>
                        </> : <div className="col-span-2" data-id="element-985">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-986">
                            Recipient contact information
                          </label>
                          <div className="space-y-2" data-id="element-987">
                            <div className="flex items-center relative" data-id="element-988">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-989">
                                <MailIcon size={16} className="text-gray-400" data-id="element-990" />
                              </div>
                              <input type="email" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Email address" value={installment.recipient?.email || ''} onChange={e => updateRecipient(installment.id, 'email', e.target.value)} data-id="element-991" />
                            </div>
                            <div className="flex items-center relative" data-id="element-992">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-993">
                                <SmartphoneIcon size={16} className="text-gray-400" data-id="element-994" />
                              </div>
                              <input type="tel" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Phone number" value={installment.recipient?.phone || ''} onChange={e => updateRecipient(installment.id, 'phone', e.target.value)} data-id="element-995" />
                            </div>
                          </div>
                        </div>}
                    </div>
                    <div data-id="element-996">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-997">
                        Description
                      </label>
                      <input type="text" className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={splitType === 'dates' ? 'e.g., First installment' : "e.g., John's share"} value={installment.description} onChange={e => updateInstallment(installment.id, 'description', e.target.value)} data-id="element-998" />
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => removeInstallment(installment.id)} className="text-gray-400 hover:text-red-500 mt-2" disabled={installments.length <= 1} data-id="element-999">
                  <TrashIcon size={18} data-id="element-1000" />
                </button>
              </div>)}
            <button type="button" onClick={addInstallment} className="mt-3 flex items-center text-sm text-blue-600 hover:text-blue-800" data-id="element-1001">
              <PlusIcon size={16} className="mr-1" data-id="element-1002" />
              Add another {splitType === 'dates' ? 'payment date' : 'recipient'}
            </button>
          </div>}
      </div>
      {/* Fee configuration section - moved to its own card */}
      {splitType === 'commission' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1003">
          <h4 className="font-medium text-gray-700 mb-3" data-id="element-1004">Fee Configuration</h4>
          <div className="space-y-3" data-id="element-1005">
            <label className="flex items-center space-x-2" data-id="element-1006">
              <input type="radio" name="feeType" checked={feeType === 'supplier'} onChange={() => setFeeType('supplier')} className="h-4 w-4 text-blue-600" data-id="element-1007" />
              <span data-id="element-1008">Partner pays the fee</span>
            </label>
            <label className="flex items-center space-x-2" data-id="element-1009">
              <input type="radio" name="feeType" checked={feeType === 'merchant'} onChange={() => setFeeType('merchant')} className="h-4 w-4 text-blue-600" data-id="element-1010" />
              <span data-id="element-1011">My business pays the fee</span>
            </label>
            <label className="flex items-center space-x-2" data-id="element-1012">
              <input type="radio" name="feeType" checked={feeType === 'split'} onChange={() => setFeeType('split')} className="h-4 w-4 text-blue-600" data-id="element-1013" />
              <span data-id="element-1014">Split the fee</span>
            </label>
            {feeType === 'split' && <div className="pl-6 mt-2" data-id="element-1015">
                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1016">
                  Partner fee percentage
                </label>
                <div className="relative w-1/3" data-id="element-1017">
                  <input type="number" className="block w-full pr-8 py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="50" min="0" max="100" defaultValue="50" data-id="element-1018" />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-1019">
                    <span className="text-gray-500" data-id="element-1020">%</span>
                  </div>
                </div>
              </div>}
          </div>
        </div>}
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} data-id="element-1021" />
    </div>;
}